import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Check, X, History, Trash } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TransactionAudit, TransactionWithInstrumentAndAccount } from "@/types";
import { AuditSheet } from "@/components/AuditSheet";
import { useAtomValue } from "jotai";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TransactionsTableProps {
  isLoading: boolean;
  transactions: TransactionWithInstrumentAndAccount[];
  onViewTransaction: (transaction: TransactionWithInstrumentAndAccount) => void;
  onConfirmTransaction: (
    transaction: TransactionWithInstrumentAndAccount,
    isConfirmed: boolean
  ) => void;
  onDeleteTransaction: (
    transaction: TransactionWithInstrumentAndAccount
  ) => void;
}

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  isLoading,
  transactions,
  onViewTransaction,
  onConfirmTransaction,
  onDeleteTransaction,
}) => {
  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionWithInstrumentAndAccount | null>(null);

  // Get current reporting period to check if it's closed
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const isPeriodClosed = !selectedReportingPeriod?.isOpen;

  const handleAuditClick = (
    transaction: TransactionWithInstrumentAndAccount
  ) => {
    setSelectedTransaction(transaction);
    setAuditOpen(true);
  };

  const handleConfirmClick = (
    transaction: TransactionWithInstrumentAndAccount
  ) => {
    if (isPeriodClosed) return;
    onConfirmTransaction(transaction, !transaction.isConfirmed);
  };

  const handleDeleteClick = (
    transaction: TransactionWithInstrumentAndAccount
  ) => {
    if (isPeriodClosed) return;
    onDeleteTransaction(transaction);
  };

  const renderTransactionData = (data: TransactionAudit) => (
    <div className="space-y-2 text-sm">
      <div>
        <strong>Key:</strong> {data.transactionKey}
      </div>
      <div>
        <strong>Date:</strong>{" "}
        {new Date(data.transactionDate).toLocaleDateString()}
      </div>
      <div>
        <strong>Type:</strong> {data.transactionType}
      </div>
      <div>
        <strong>Account:</strong> {data.accountName}
      </div>
      <div>
        <strong>Amount:</strong> {formatAmount(data.baseAmount, data.currency)}
      </div>
      <div>
        <strong>Description:</strong> {data.description ?? "N/A"}
      </div>
      <div>
        <strong>Confirmed:</strong> {data.isConfirmed ? "Yes" : "No"}
      </div>
    </div>
  );
  // Helper function to format currency amounts
  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  // Helper function to get badge variant based on transaction status
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Helper function to get badge variant based on transaction type
  const getTypeBadgeVariant = (type: string) => {
    switch (type.toLowerCase()) {
      case "investment":
        return "default";
      case "dividend":
        return "secondary";
      case "purchase":
        return "outline";
      default:
        return "secondary";
    }
  };

  // Helper function to get confirmation badge variant
  const getConfirmationBadgeVariant = (isConfirmed: boolean) => {
    return isConfirmed ? "success" : "destructive";
  };

  return (
    <div className="max-h-[80vh] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-background z-10">
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Date</TableHead>
            {/* <TableHead>Description</TableHead> */}
            <TableHead>Type</TableHead>
            <TableHead>Account</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Confirmed</TableHead>
            <TableHead className="w-32 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-4">
                Loading transactions...
              </TableCell>
            </TableRow>
          ) : transactions.length > 0 ? (
            transactions.map((transaction) => (
              <TableRow key={transaction.transactionId}>
                <TableCell>{transaction.transactionKey}</TableCell>
                <TableCell>
                  {new Date(transaction.transactionDate).toLocaleDateString()}
                </TableCell>
                {/* <TableCell>{transaction.transactionType}</TableCell> */}
                <TableCell>
                  <Badge
                    variant={getTypeBadgeVariant(transaction.transactionType)}
                  >
                    {transaction.transactionType}
                  </Badge>
                </TableCell>
                <TableCell>{transaction.account?.name}</TableCell>
                <TableCell className="text-right">
                  <span
                    className={
                      transaction.baseAmount < 0
                        ? "text-destructive"
                        : "text-success"
                    }
                  >
                    {formatAmount(transaction.baseAmount, transaction.currency)}
                  </span>
                </TableCell>
                <TableCell>{transaction.description ?? ""}</TableCell>
                <TableCell>
                  <Badge
                    variant={getConfirmationBadgeVariant(
                      transaction.isConfirmed
                    )}
                  >
                    {transaction.isConfirmed ? "Confirmed" : "Unconfirmed"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex space-x-1 justify-end">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleAuditClick(transaction)}
                      title="View Audit History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={
                              transaction.isConfirmed ? "outline" : "default"
                            }
                            size="icon"
                            onClick={() => handleConfirmClick(transaction)}
                            disabled={isPeriodClosed}
                            title={
                              isPeriodClosed
                                ? "Cannot modify confirmation when reporting period is closed"
                                : transaction.isConfirmed
                                ? "Unconfirm"
                                : "Confirm"
                            }
                          >
                            {transaction.isConfirmed ? (
                              <X className="h-4 w-4" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        {isPeriodClosed && (
                          <TooltipContent>
                            <p>
                              Cannot modify confirmation when reporting period
                              is closed
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => handleDeleteClick(transaction)}
                            disabled={isPeriodClosed}
                            title={
                              isPeriodClosed
                                ? "Cannot delete transaction when reporting period is closed"
                                : "Delete Transaction"
                            }
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        {isPeriodClosed && (
                          <TooltipContent>
                            <p>
                              Cannot delete transaction when reporting period is
                              closed
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-4">
                No transactions found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedTransaction && (
        <AuditSheet
          open={auditOpen}
          onOpenChange={setAuditOpen}
          title="Transaction"
          entityId={selectedTransaction.transactionId}
          entityType="transaction"
          renderCurrentData={renderTransactionData}
        />
      )}
    </div>
  );
};
